import type { Dict } from "../core.js";
import type { lab as vi } from "../vi/lab.js";

export const lab: Dict<typeof vi> = {
  catalogServiceCount: "{count} services",
  configureService: "Configure service",
  supportedApps: "Used by:",
  supportedEnvironments: "Supports:",
  serviceNoConnections: "No test connection yet. Open Configure service to set one up.",
  serviceNoConnectionsInEnvironment:
    "No test connection in the selected environment. Open Configure service to set one up.",

  appEmpty:
    "This app has no connection available for testing yet. Configure a supported integration to get started.",
  filterApp: "Choose an app",
  labCount: "{count} test connections",
  filters: "Lab filters",
  filterCategory: "Service type",
  allCategories: "All service types",
  filterEnvironment: "Environment",
  allEnvironments: "All environments",
  searchLabs: "Find a lab",
  searchPlaceholder: "Connection or provider name…",
  appScopeHelp:
    "Filter by integrations supported by the app. Test accounts and fixtures stay separate in HQ.",
  resetFilters: "Clear filters",
  legacyKind: "Lab type from this link uses a previous connection grouping.",
  clearKind: "Remove this limit",
  retryLabs: "Retry loading",
  allServices: "All services",
  resultCount: "{count} connections · {products} products",
  noMatchingLabs: "No labs match the selected filters.",
  configureIntegration: "Configure an integration",
  openNamedLab: "Open {name} lab — {connection} — {environment}",
  testStatusHelp: "The connection status is a test result, not an app enabled/disabled setting.",

  fixtureExpired: "Expired",
  indexTitle: "Labs",
  indexIntro:
    "Choose an app, find a service and open the connection to test. Bube test accounts and fixtures are managed separately in HQ.",
  indexEmpty: "No credential set has a lab yet.",
  openLab: "Open lab",
  loading: "Loading…",
  loadFailed: "Could not load: {error}",
  noPermission: "Your role cannot view labs (needs « hq.lab.read »).",
  fixturesTitle: "Test data",
  fixturesIntro:
    "Bube-owned test identities acting as a customer (test tax code, test e-invoice account, test bank account). Not tied to any credential set — the lab picks a fixture and a set separately, so a live fixture can test Bube's production keys too. Secret fields never come back to the page.",
  fixturesEmpty: "No fixtures yet.",
  addFixture: "Add fixture",
  colLabel: "Name",
  colProduct: "Product",
  colEnvironment: "Environment",
  colOwner: "Owner",
  colStatus: "Status",
  colAllowlist: "Allowed targets",
  colSecrets: "Saved secret fields",
  colLastUsed: "Last used",
  colActions: "Actions",
  never: "never",
  env_sandbox: "Sandbox",
  env_live: "Production",
  owner_organization: "Bube",
  owner_team: "Team {id}",
  owner_user: "User {id}",
  status_active: "Active",
  status_disabled: "Disabled",
  status_expired: "Expired",
  edit: "Edit",
  disable: "Disable",
  enable: "Enable",
  remove: "Delete",
  removeConfirm: "Click again to delete",
  viewRuns: "Runs",
  readOnlyRole: "Your role is read-only — editing fixtures needs « hq.lab.write ».",
  formCreateTitle: "New fixture",
  formEditTitle: "Edit fixture « {label} »",
  fieldProduct: "Product",
  fieldEnvironment: "Provider environment",
  fieldLabel: "Fixture name",
  fieldLabelHint: "WRITE operations with a live fixture require retyping exactly this name.",
  fieldOwnerType: "Owner",
  fieldOwnerId: "Owner id (user email / team code)",
  fieldsTitle: "Test account fields",
  fieldsFreeHint: "This product declares no customer fields — one key=value per line.",
  fieldsPublic: "Non-secret fields",
  fieldsSecret: "Secret fields",
  secretSaved: "saved {hint} — leave empty to keep",
  forgetSecret: "Remove this field",
  fieldTarget_taxCode: "Allowed tax codes (allowlist)",
  fieldTarget_accountNumber: "Allowed account / VA numbers (allowlist)",
  fieldTarget_companyXid: "Allowed Bank Hub companies (company_xid)",
  fieldTargetHint:
    "Comma separated. The first one is the default target of a run. A live fixture must declare it to WRITE to this target.",
  fieldWritesPerDay: "WRITE cap per 24 hours",
  fieldWritesPerDayHint: "Empty = no cap.",
  fieldExpiresAt: "Expires at",
  fieldOperations: "Allowed operations",
  fieldOperationsHint:
    "The guard only runs operations whose capability is in this list. Empty = READ ONLY (every write is blocked).",
  fieldAllowRawReads: "Allow raw READ-ONLY APIs",
  fieldAllowRawReadsHint:
    "Provider APIs that map to no standard capability (lists, templates, history…). Raw WRITE APIs are always blocked.",
  save: "Save",
  saving: "Saving…",
  cancel: "Cancel",
  saveFailed: "Could not save: {error}",
  runsTitle: "Test runs",
  runsIntro:
    "Every lab run (including runs the guard blocked) and every « Run test » of a set is a test run — that set's certification.",
  runsEmpty: "No test runs yet.",
  runsFilterFixture: "Fixture",
  runsFilterAll: "All",
  colTime: "At",
  colFixture: "Fixture",
  colSet: "Connection",
  colOperation: "Operation",
  colOutcome: "Outcome",
  colReason: "Details",
  colActor: "Run by",
  outcome_passed: "Passed",
  outcome_failed: "Failed",
  outcome_blocked: "Blocked",
  confirmedIdentifier: "name retyped",
  writeRun: "write",
  setTest: "Set test",
  noFixture: "—",
  reason_LAB_OPERATION_EFFECT_UNKNOWN: "The operation does not declare read / write — not run.",
  reason_LAB_FIXTURE_REQUIRED: "A write, raw or test-account operation needs a fixture.",
  reason_LAB_OPERATION_NOT_ALLOWED:
    "The operation is not in the fixture's allowed list (empty list = read only; raw writes are always blocked).",
  reason_LAB_RAW_READ_NOT_ALLOWED:
    "A raw read-only API needs the fixture to enable « Allow raw READ-ONLY APIs ».",
  reason_LAB_FIXTURE_INACTIVE: "The fixture is disabled or expired.",
  reason_LAB_PRODUCT_MISMATCH: "The fixture belongs to another product than the set.",
  reason_LAB_ENVIRONMENT_MISMATCH: "The fixture's environment differs from the set's.",
  reason_LAB_CONFIRM_IDENTIFIER_REQUIRED:
    "A write with a live fixture needs the fixture name retyped.",
  reason_LAB_TAX_CODE_NOT_ALLOWED: "The run's tax code is not in the fixture's allowlist.",
  reason_LAB_TARGET_NOT_ALLOWED:
    "The run's target (account / company) is not in the fixture's allowlist.",
  reason_LAB_LIMIT_REACHED: "The fixture's 24-hour write cap is reached.",
  pickFixture: "Fixture",
  pickFixtureNone: "No fixture (only READ operations that need no test account can run)",
  pickFixtureHint:
    "Only fixtures of the same product + environment as the set. Write / raw / test-account operations require a fixture. Fields typed on the page win over the fixture's.",
  pickFixtureEmpty: "No fixture for this product + environment yet.",
  manageFixtures: "Manage fixtures",
  confirmIdentifier: "Retype the fixture name to write",
};
